import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { Plus, Trash2, Save, ToggleLeft, ToggleRight, Copy, Server, Clock, X } from 'lucide-react';

const DEFAULT_BODY = '{\n  "success": true,\n  "data": {}\n}';

export default function MockPanel({ endpoint, projectId, onEndpointSelect }) {
  const [rules, setRules] = useState([]);
  const [selected, setSelected] = useState(null);
  const [editing, setEditing] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [newRule, setNewRule] = useState({ rule_name:'Default Mock', response_status:200, response_body:DEFAULT_BODY, response_headers:'{"Content-Type":"application/json"}', delay_ms:0 });
  const [endpoints, setEndpoints] = useState([]);
  const [loadingEndpoints, setLoadingEndpoints] = useState(false);

  const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';
  const apiOrigin = (() => {
    try {
      return new URL(apiBase).origin;
    } catch {
      return apiBase.replace(/\/api$/, '');
    }
  })();
  const mockUrl = `${apiOrigin}/mock/${projectId}${endpoint?.path||''}`;

  useEffect(() => {
    setSelected(null);
    setEditing(null);
    setRules([]);
    if (endpoint?.id) load();
  }, [endpoint?.id]);

  useEffect(() => { if (projectId) loadEndpoints(); }, [projectId]);

  async function load() {
    if (!endpoint?.id) return;
    try { const r = await api.get(`/endpoints/${endpoint.id}/mock-rules`); setRules(r.data); if (r.data.length && !selected) sel(r.data[0]); } catch {}
  }

  async function loadEndpoints() {
    setLoadingEndpoints(true);
    try {
      const r = await api.get(`/projects/${projectId}/endpoints`);
      setEndpoints(r.data.endpoints || []);
    } catch {} finally { setLoadingEndpoints(false); }
  }

  function sel(rule) {
    setSelected(rule);
    setEditing({ ...rule, response_headers: typeof rule.response_headers==='string' ? rule.response_headers : JSON.stringify(rule.response_headers,null,2) });
  }

  function selectEndpoint(id) {
    if (!id) return;
    const ep = endpoints.find(item => item.id === id);
    if (!ep) return;
    if (onEndpointSelect) {
      onEndpointSelect(ep);
    }
  }

  async function create(e) {
    e.preventDefault();
    try {
      let headers; try { headers=JSON.parse(newRule.response_headers); } catch { toast.error('Invalid JSON in headers'); return; }
      const r = await api.post(`/endpoints/${endpoint.id}/mock-rules`, { ...newRule, response_headers:headers });
      toast.success('Rule created!');
      setShowCreate(false);
      setNewRule({ rule_name:'Default Mock', response_status:200, response_body:DEFAULT_BODY, response_headers:'{"Content-Type":"application/json"}', delay_ms:0 });
      load(); sel(r.data);
    } catch { toast.error('Failed'); }
  }

  async function save() {
    try {
      let headers; try { headers=JSON.parse(editing.response_headers); } catch { toast.error('Invalid headers JSON'); return; }
      await api.put(`/mock-rules/${editing.id}`, { ...editing, response_headers:headers });
      toast.success('Saved!'); load();
    } catch { toast.error('Failed'); }
  }

  async function toggle(rule, e) {
    e.stopPropagation();
    try { await api.put(`/mock-rules/${rule.id}`, { ...rule, enabled:!rule.enabled }); load(); } catch {}
  }

  async function del(id, e) {
    e.stopPropagation();
    if (!window.confirm('Delete rule?')) return;
    try { await api.delete(`/mock-rules/${id}`); toast.success('Deleted'); if (selected?.id===id){setSelected(null);setEditing(null);} load(); } catch {}
  }

  const SC = { 200:'#16a34a', 201:'#16a34a', 204:'#16a34a', 400:'#ea580c', 401:'#ea580c', 403:'#ea580c', 404:'#dc2626', 500:'#dc2626' };

  if (!endpoint && !loadingEndpoints && !endpoints.length) return (
    <div style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', flexDirection:'column', gap:10, color:'var(--text-3)' }}>
      <Server size={40} style={{ opacity:.2 }}/><div style={{ fontSize:13 }}>No endpoints available to configure mocks</div>
    </div>
  );

  return (
    <div style={{ display:'flex', height:'100%', overflow:'hidden' }}>
      {/* Rules list */}
      <div style={{ width:240, borderRight:'1px solid var(--border)', display:'flex', flexDirection:'column', background:'var(--bg-secondary)' }}>
        <div style={{ padding:'12px 12px 8px', borderBottom:'1px solid var(--border)', flexShrink:0 }}>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:6 }}>
            <span style={{ fontSize:14, fontWeight:700 }}>Mock Rules</span>
            <button className="btn-icon" onClick={() => setShowCreate(true)}><Plus size={14}/></button>
          </div>
          <div style={{ display:'flex', flexDirection:'column', gap:8, marginBottom:10 }}>
            <label style={{ fontSize:11, color:'var(--text-3)', fontWeight:700 }}>Endpoint</label>
            <select
              style={{ width:'100%', padding:'8px 10px', borderRadius:6, border:'1px solid var(--border)', background:'var(--bg-base)', color:'var(--text-1)' }}
              value={endpoint?.id || ''}
              onChange={e => selectEndpoint(e.target.value)}
              disabled={loadingEndpoints}
            >
              <option value="">Select endpoint</option>
              {endpoints.map(ep => (
                <option key={ep.id} value={ep.id}>{ep.method} {ep.path}</option>
              ))}
            </select>
          </div>
          <div style={{ fontSize:11, color:'var(--text-3)' }}>
            Mock URL: <span style={{ color:'var(--accent)', fontFamily:'var(--mono)', fontSize:10 }}>/mock/{projectId}{endpoint?.path || ''}</span>
            <button className="btn-icon" style={{ padding:2, marginLeft:3 }} onClick={() => { navigator.clipboard.writeText(mockUrl); toast.success('Copied!'); }}><Copy size={10}/></button>
          </div>
        </div>
        <div style={{ flex:1, overflow:'auto', padding:'6px' }}>
          {!rules.length && <div style={{ padding:'16px 8px', color:'var(--text-3)', fontSize:12, textAlign:'center' }}>No mock rules yet</div>}
          {rules.map(rule => (
            <div key={rule.id} style={{ padding:'8px 10px', borderRadius:7, cursor:'pointer', marginBottom:1, background: selected?.id===rule.id ? 'var(--accent-light)':'transparent', transition:'background .1s' }}
              onClick={() => sel(rule)}>
              <div style={{ display:'flex', alignItems:'center', gap:7, marginBottom:4 }}>
                <span style={{ fontFamily:'var(--mono)', fontWeight:700, fontSize:12, color:SC[rule.response_status]||'#6b7280' }}>{rule.response_status}</span>
                <span style={{ flex:1, fontSize:12, fontWeight:500, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{rule.rule_name}</span>
              </div>
              <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                {rule.delay_ms > 0 && <span style={{ fontSize:10, color:'var(--text-3)', display:'flex', alignItems:'center', gap:2 }}><Clock size={9}/>{rule.delay_ms}ms</span>}
                <button style={{ background:'none', border:'none', cursor:'pointer', padding:2, marginLeft:'auto' }} onClick={e=>toggle(rule,e)}>
                  {rule.enabled ? <ToggleRight size={16} style={{ color:'var(--green)' }}/> : <ToggleLeft size={16} style={{ color:'var(--text-3)' }}/>}
                </button>
                <button className="btn-icon" style={{ padding:2 }} onClick={e=>del(rule.id,e)}><Trash2 size={11} style={{ color:'var(--red)' }}/></button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Editor */}
      {editing ? (
        <div style={{ flex:1, overflow:'auto', padding:20 }}>
          <div style={{ maxWidth:680 }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:18 }}>
              <input style={{ fontSize:16, fontWeight:700, background:'transparent', border:'none', padding:0, fontFamily:'var(--font)', flex:1 }}
                value={editing.rule_name} onChange={e=>setEditing(v=>({...v,rule_name:e.target.value}))} placeholder="Rule name"/>
              <button className="btn btn-primary btn-sm" onClick={save}><Save size={13}/> Save Rule</button>
            </div>
            <div className="form-row" style={{ marginBottom:14 }}>
              <div className="form-group">
                <label className="form-label">Response Status</label>
                <select value={editing.response_status} onChange={e=>setEditing(v=>({...v,response_status:parseInt(e.target.value)}))} style={{ color:SC[editing.response_status]||'var(--text-1)', fontWeight:700, fontFamily:'var(--mono)' }}>
                  {[200,201,204,301,302,400,401,403,404,409,422,429,500,502,503].map(s=><option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Response Delay (ms)</label>
                <input type="number" min={0} max={30000} value={editing.delay_ms} onChange={e=>setEditing(v=>({...v,delay_ms:parseInt(e.target.value)||0}))}/>
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Response Headers (JSON)</label>
              <textarea className="mono" style={{ height:80, fontSize:12 }} value={editing.response_headers} onChange={e=>setEditing(v=>({...v,response_headers:e.target.value}))}/>
            </div>
            <div className="form-group">
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:5 }}>
                <label className="form-label" style={{ margin:0 }}>Response Body</label>
                <button className="btn btn-xs btn-secondary" onClick={() => { try { setEditing(v=>({...v,response_body:JSON.stringify(JSON.parse(v.response_body),null,2)})); } catch { toast.error('Invalid JSON'); } }}>Format</button>
              </div>
              <textarea className="code-block" style={{ height:240, width:'100%', resize:'vertical', border:'1px solid var(--border)', borderRadius:6, padding:12, fontSize:12, lineHeight:1.7 }}
                value={editing.response_body} onChange={e=>setEditing(v=>({...v,response_body:e.target.value}))}/>
            </div>
          </div>
        </div>
      ) : (
        <div style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', flexDirection:'column', gap:10, color:'var(--text-3)' }}>
          <Server size={40} style={{ opacity:.2 }}/><div style={{ fontSize:13 }}>Select a rule to edit</div>
        </div>
      )}

      {showCreate && (
        <div className="modal-overlay" onClick={() => setShowCreate(false)}>
          <div className="modal" onClick={e=>e.stopPropagation()}>
            <div className="modal-header"><div className="modal-title">New Mock Rule</div><button className="btn-icon" onClick={()=>setShowCreate(false)}><X size={16}/></button></div>
            <form onSubmit={create}>
              <div className="form-row">
                <div className="form-group"><label className="form-label">Rule Name</label>
                  <input value={newRule.rule_name} onChange={e=>setNewRule(r=>({...r,rule_name:e.target.value}))} required autoFocus/></div>
                <div className="form-group"><label className="form-label">Status Code</label>
                  <select value={newRule.response_status} onChange={e=>setNewRule(r=>({...r,response_status:parseInt(e.target.value)}))}>
                    {[200,201,204,400,401,403,404,422,500].map(s=><option key={s} value={s}>{s}</option>)}
                  </select></div>
              </div>
              <div className="form-group"><label className="form-label">Response Body (JSON)</label>
                <textarea className="code-block" style={{ height:140, width:'100%', border:'1px solid var(--border)', padding:10, fontSize:12, resize:'vertical', lineHeight:1.6 }}
                  value={newRule.response_body} onChange={e=>setNewRule(r=>({...r,response_body:e.target.value}))}/></div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={()=>setShowCreate(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary">Create Rule</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
